import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, addDoc, getDocs, orderBy, query, updateDoc, doc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';

const TOOLS = [
  {
    id: 'campaign',
    icon: '📅',
    label: 'Campaign Plan',
    color: '#2AD5FF',
    description: 'Build a full monthly marketing plan with objectives, audience, messaging, channel strategy, and content calendar.',
    fields: [
      { key: 'month',    label: 'Target Month',         type: 'text',     placeholder: 'e.g. May 2026' },
      { key: 'goal',     label: 'Campaign Goal',         type: 'text',     placeholder: 'e.g. Increase weekend foot traffic by 20%' },
      { key: 'audience', label: 'Target Audience',       type: 'text',     placeholder: 'e.g. Pocono visitors, local families, corporate groups' },
      { key: 'budget',   label: 'Budget (optional)',     type: 'text',     placeholder: 'e.g. $500/month' },
      { key: 'notes',    label: 'Additional Notes',      type: 'textarea', placeholder: 'Upcoming events, specials, or seasonal info...' },
    ],
  },
  {
    id: 'content',
    icon: '✍️',
    label: 'Content Creation',
    color: '#563DE4',
    description: 'Draft blog posts, social captions, email newsletters, landing pages, and press releases tailored to Skylanes.',
    fields: [
      { key: 'type',    label: 'Content Type',    type: 'select',   options: ['Blog Post', 'Social Caption', 'Email Newsletter', 'Landing Page', 'Press Release'] },
      { key: 'topic',   label: 'Topic / Subject', type: 'text',     placeholder: 'e.g. Glow Bowling Night, Kids Bowl Free, Spring League' },
      { key: 'tone',    label: 'Tone',            type: 'select',   options: ['Fun & Casual', 'Exciting & Energetic', 'Family-Friendly', 'Professional'] },
      { key: 'details', label: 'Key Details',     type: 'textarea', placeholder: 'Date, time, pricing, special offers, call to action...' },
    ],
  },
  {
    id: 'email',
    icon: '📧',
    label: 'Email Sequences',
    color: '#FFD200',
    description: 'Design multi-email flows with full copy, timing, and branching logic for onboarding, re-engagement, and event promotion.',
    fields: [
      { key: 'type',    label: 'Sequence Type',   type: 'select',   options: ['New Member Welcome', 'Event Promotion', 'Re-engagement', 'League Sign-up', 'Birthday Offer'] },
      { key: 'emails',  label: 'Number of Emails', type: 'select',  options: ['3 emails', '5 emails', '7 emails'] },
      { key: 'trigger', label: 'Trigger / Goal',  type: 'text',     placeholder: 'e.g. Someone joins the mailing list, hasn\'t visited in 60 days' },
      { key: 'offer',   label: 'Offer or Hook',   type: 'text',     placeholder: 'e.g. Free game of bowling, 20% off food & drinks' },
      { key: 'notes',   label: 'Notes',           type: 'textarea', placeholder: 'Anything specific to include...' },
    ],
  },
  {
    id: 'seo',
    icon: '🔍',
    label: 'SEO Audit',
    color: '#27ae60',
    description: 'Check how Skylanes ranks in search results, find keyword gaps, and get a prioritised action plan to drive more organic traffic.',
    fields: [
      { key: 'url',      label: 'Website URL',      type: 'text',     placeholder: 'https://skylanesbowling.com' },
      { key: 'keywords', label: 'Target Keywords',  type: 'text',     placeholder: 'e.g. bowling alley Poconos, bowling near East Stroudsburg' },
      { key: 'focus',    label: 'Focus Area',       type: 'select',   options: ['Full Audit', 'Local SEO', 'Content Gaps', 'Competitor Comparison', 'Technical SEO'] },
      { key: 'notes',    label: 'Notes',            type: 'textarea', placeholder: 'Any specific pages or issues you\'ve noticed...' },
    ],
  },
  {
    id: 'competitive',
    icon: '🏆',
    label: 'Competitive Brief',
    color: '#e94560',
    description: 'Research other Poconos bowling alleys and entertainment venues — find positioning gaps and angles they haven\'t claimed.',
    fields: [
      { key: 'competitors', label: 'Known Competitors',  type: 'text',     placeholder: 'e.g. Pocono Bowl, other local bowling alleys' },
      { key: 'focus',       label: 'Focus',              type: 'select',   options: ['Pricing & Offers', 'Social Media', 'Website & SEO', 'Events & Specials', 'Full Comparison'] },
      { key: 'question',    label: 'Key Question',       type: 'textarea', placeholder: 'e.g. Why would someone choose Skylanes over a competitor?' },
    ],
  },
  {
    id: 'brand',
    icon: '🎨',
    label: 'Brand Review',
    color: '#16a085',
    description: 'Review your marketing content against your brand voice — flag deviations and get specific before/after fixes.',
    fields: [
      { key: 'content', label: 'Content to Review', type: 'textarea', placeholder: 'Paste your post, email, ad copy, or web content here...' },
      { key: 'channel', label: 'Channel',           type: 'select',   options: ['Facebook Post', 'Email', 'Website Copy', 'Flyer / Print', 'Press Release'] },
      { key: 'concern', label: 'Specific Concern',  type: 'text',     placeholder: 'e.g. Is this on-brand? Too formal? Missing a CTA?' },
    ],
  },
];

const STATUS_COLORS = {
  pending:     { bg: 'rgba(255,210,0,0.15)',    color: '#FFD200' },
  'in-progress': { bg: 'rgba(42,213,255,0.15)', color: '#2AD5FF' },
  complete:    { bg: 'rgba(39,174,96,0.15)',    color: '#27ae60' },
};

/* ── Request Form ── */
function RequestForm({ tool, onSubmit, onCancel }) {
  const [values, setValues] = useState(
    Object.fromEntries(tool.fields.map(f => [f.key, f.options ? f.options[0] : '']))
  );

  const handleSubmit = e => {
    e.preventDefault();
    onSubmit(values);
  };

  return (
    <form className="mk-form" onSubmit={handleSubmit}>
      {tool.fields.map(f => (
        <div className="mk-field" key={f.key}>
          <label className="mk-field-label">{f.label}</label>
          {f.type === 'textarea' ? (
            <textarea
              className="mk-input"
              rows={3}
              placeholder={f.placeholder}
              value={values[f.key]}
              onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
            />
          ) : f.type === 'select' ? (
            <select
              className="mk-input mk-select"
              value={values[f.key]}
              onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
            >
              {f.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input
              className="mk-input"
              type="text"
              placeholder={f.placeholder}
              value={values[f.key]}
              onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
            />
          )}
        </div>
      ))}
      <div className="mk-form-actions">
        <button type="button" className="ah-btn ah-btn-outline ah-btn-sm" onClick={onCancel}>Cancel</button>
        <button type="submit" className="ah-btn ah-btn-primary ah-btn-sm">Submit Request</button>
      </div>
    </form>
  );
}

/* ── Result Editor ── */
function ResultEditor({ request, onSave, onClose }) {
  const [result, setResult] = useState(request.result || '');
  return (
    <div className="mk-result-editor">
      <div className="mk-result-label">Paste AI-generated output below:</div>
      <textarea
        className="mk-input mk-result-textarea"
        rows={8}
        value={result}
        onChange={e => setResult(e.target.value)}
        placeholder="Paste the generated content here to save it..."
      />
      <div className="mk-form-actions">
        <button className="ah-btn ah-btn-outline ah-btn-sm" onClick={onClose}>Cancel</button>
        <button className="ah-btn ah-btn-success ah-btn-sm" onClick={() => onSave(result)}>Save Output</button>
      </div>
    </div>
  );
}

/* ── Main Component ── */
function AdminMarketing() {
  const [user, setUser]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [requests, setRequests]   = useState([]);
  const [activeTool, setActiveTool] = useState(null);
  const [editResult, setEditResult] = useState(null);
  const [toast, setToast]         = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, currentUser => {
      if (!currentUser) navigate('/admin/login');
      else { setUser(currentUser); loadRequests(); }
      setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadRequests = async () => {
    try {
      const q = query(collection(db, 'marketing_requests'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading requests:', err);
    }
  };

  const handleSubmitRequest = async (toolId, values) => {
    const tool = TOOLS.find(t => t.id === toolId);
    try {
      const docRef = await addDoc(collection(db, 'marketing_requests'), {
        toolId,
        toolLabel: tool.label,
        toolIcon: tool.icon,
        values,
        status: 'pending',
        result: '',
        createdAt: new Date().toISOString(),
        createdBy: user.email,
      });
      setRequests(prev => [{
        id: docRef.id, toolId, toolLabel: tool.label, toolIcon: tool.icon,
        values, status: 'pending', result: '', createdAt: new Date().toISOString(),
      }, ...prev]);
      setActiveTool(null);
      showToast(`${tool.label} request submitted!`);
    } catch (err) {
      showToast('Error saving request: ' + err.message, 'error');
    }
  };

  const handleSaveResult = async (requestId, result) => {
    try {
      await updateDoc(doc(db, 'marketing_requests', requestId), {
        result,
        status: 'complete',
        completedAt: new Date().toISOString(),
      });
      setRequests(prev => prev.map(r =>
        r.id === requestId ? { ...r, result, status: 'complete' } : r
      ));
      setEditResult(null);
      showToast('Output saved!');
    } catch (err) {
      showToast('Error saving output: ' + err.message, 'error');
    }
  };

  const handleUpdateStatus = async (requestId, status) => {
    try {
      await updateDoc(doc(db, 'marketing_requests', requestId), { status });
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status } : r));
    } catch (err) {
      showToast('Error updating status', 'error');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/admin/login');
  };

  if (loading) return <div className="admin-loading">Loading...</div>;
  if (!user) return null;

  const pendingCount  = requests.filter(r => r.status === 'pending').length;
  const completeCount = requests.filter(r => r.status === 'complete').length;

  return (
    <div className="ah-layout">
      <AdminSidebar user={user} onLogout={handleLogout} />

      <main className="ah-main">
        {/* Top bar */}
        <div className="ah-topbar">
          <div>
            <h1 className="ah-page-title">📣 Marketing Manager</h1>
            <p className="ah-page-sub">Campaign planning, content creation, SEO, and more — all for Skylanes</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: 'rgba(233,233,233,0.5)' }}>
            <span style={{ color: '#FFD200', fontWeight: 700 }}>{pendingCount} pending</span>
            <span style={{ color: '#27ae60', fontWeight: 700 }}>{completeCount} complete</span>
          </div>
        </div>

        {/* Tool cards */}
        <div className="mk-tools-grid">
          {TOOLS.map(tool => (
            <div
              key={tool.id}
              className={`mk-tool-card${activeTool === tool.id ? ' mk-tool-active' : ''}`}
              style={{ '--tc': tool.color }}
            >
              <div className="mk-tool-header">
                <div className="mk-tool-icon">{tool.icon}</div>
                <div>
                  <div className="mk-tool-label">{tool.label}</div>
                  <div className="mk-tool-count">
                    {requests.filter(r => r.toolId === tool.id).length} requests
                  </div>
                </div>
                <button
                  className="ah-btn ah-btn-sm ah-btn-primary mk-tool-btn"
                  onClick={() => setActiveTool(activeTool === tool.id ? null : tool.id)}
                >
                  {activeTool === tool.id ? '✕ Close' : '+ New'}
                </button>
              </div>
              <p className="mk-tool-desc">{tool.description}</p>
              {activeTool === tool.id && (
                <RequestForm
                  tool={tool}
                  onSubmit={vals => handleSubmitRequest(tool.id, vals)}
                  onCancel={() => setActiveTool(null)}
                />
              )}
            </div>
          ))}
        </div>

        {/* Requests log */}
        {requests.length > 0 && (
          <div className="mk-requests-section">
            <div className="mk-requests-header">
              <h3>📋 Request History</h3>
            </div>
            <div className="mk-requests-list">
              {requests.map(req => {
                const sc = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
                return (
                  <div key={req.id} className="mk-request-card">
                    <div className="mk-req-top">
                      <div className="mk-req-title">
                        <span className="mk-req-icon">{req.toolIcon}</span>
                        <span className="mk-req-label">{req.toolLabel}</span>
                        <span className="mk-req-date">
                          {new Date(req.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <div className="mk-req-actions">
                        <select
                          className="mk-status-select"
                          value={req.status}
                          style={{ background: sc.bg, color: sc.color }}
                          onChange={e => handleUpdateStatus(req.id, e.target.value)}
                        >
                          <option value="pending">Pending</option>
                          <option value="in-progress">In Progress</option>
                          <option value="complete">Complete</option>
                        </select>
                        <button
                          className="ah-btn ah-btn-sm ah-btn-outline"
                          onClick={() => setEditResult(editResult === req.id ? null : req.id)}
                        >
                          {req.result ? '📝 Edit Output' : '📥 Add Output'}
                        </button>
                      </div>
                    </div>

                    {/* Brief summary */}
                    <div className="mk-req-brief">
                      {Object.entries(req.values).map(([k, v]) => v ? (
                        <div key={k} className="mk-req-field">
                          <span className="mk-req-field-key">{k}:</span>
                          <span className="mk-req-field-val">{v}</span>
                        </div>
                      ) : null)}
                    </div>

                    {/* Result editor */}
                    {editResult === req.id && (
                      <ResultEditor
                        request={req}
                        onSave={result => handleSaveResult(req.id, result)}
                        onClose={() => setEditResult(null)}
                      />
                    )}

                    {/* Saved result */}
                    {req.result && editResult !== req.id && (
                      <div className="mk-req-result">
                        <div className="mk-req-result-label">✅ Output</div>
                        <div className="mk-req-result-text">{req.result}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Toast */}
      {toast && (
        <div className={`ah-toast-container`}>
          <div className={`ah-toast ah-toast-${toast.type}`}>{toast.msg}</div>
        </div>
      )}
    </div>
  );
}

export default AdminMarketing;
